import type { BackendTypes } from '@tf/api';
import type { TFEmptyStateMessageProps } from '@tf/ui';
import { Button, Center, Icon, TFCard, TFDataTable, TFEmptyStateMessage, TFLoadingOverlay, TFNotifier } from '@tf/ui';
import { fmt, S, saveFile } from '@tf/utils';
import type React from 'react';

import { useAccountsExportHistoryQuery, useDownloadAccountsExportMutation } from '@/core/api/dataExport';

export const ExportHistoryTable: React.FC = () => {
	const exportHistoryQuery = useAccountsExportHistoryQuery();

	const downloadAccountsExportMutation = useDownloadAccountsExportMutation();
	const downloadResult = (item: BackendTypes.AccountsExport) => {
		downloadAccountsExportMutation.mutate(
			{ jobId: encodeURIComponent(item.key) },
			{
				onSuccess: (file) => {
					saveFile(
						file,
						item.result?.filename ?? `Data Export of ${fmt.toDate(item.createdAt, { preset: 'full_date' })}.xlsx`,
					);
				},
				onError: () => {
					TFNotifier.error('Failed to download file');
				},
			},
		);
	};

	const errorMessage: TFEmptyStateMessageProps = {
		icon: <Icon.IconUnlink />,
		title: 'Error',
		description: 'Please try again',
	};

	if (exportHistoryQuery.error) {
		return (
			<TFCard>
				<Center h={300}>
					<TFEmptyStateMessage {...errorMessage} />
				</Center>
			</TFCard>
		);
	}

	if (exportHistoryQuery.data) {
		return (
			<TFDataTable<BackendTypes.AccountsExport>
				hideSearchbar
				name="export-history"
				data={exportHistoryQuery.data}
				defs={[
					{
						header: 'Date',
						accessorKey: 'createdAt',
						enableSorting: true,
						size: 200,
						Cell: ({ row }) => {
							return fmt.toDate(row.original.createdAt, { preset: 'full_date_with_time' });
						},
					},
					{
						header: 'Status',
						accessorKey: 'status',
						enableSorting: true,
						size: 200,
						Cell: ({ row }) => {
							return S.prettify(row.original.status);
						},
					},
					{
						header: 'Result',
						accessorKey: 'result',
						enableSorting: true,
						size: 200,
						Cell: ({ row }) => {
							if (!row.original.result) {
								return null;
							}

							return (
								<Button
									variant="subtle"
									size="xs"
									disabled={row.original.status !== 'SUCCESS'}
									onClick={() => downloadResult(row.original)}
								>
									Download
								</Button>
							);
						},
					},
				]}
			/>
		);
	}

	return <TFLoadingOverlay size={28} h={300} />;
};
